import { Button, CircularProgress, Dialog, DialogContent, DialogTitle, Divider, Typography } from '@mui/material';
import { Box } from '@mui/system';
import CustomMultipleSelect from 'Components/CustomMultipleSelect/CustomMultipleSelect';
import CustomSelect from 'Components/CustomSelect/CustomSelect';
import { Controller, useForm } from 'react-hook-form';
import CoursesTable, { type CoursesTableHandle } from './CoursesTable';
import useTerminalsApi from 'Hooks/useTerminalsApi';
import useDepartmentsApi from 'Hooks/useDepartmentsApi';
import useJobTitlesApi from 'Hooks/useJobTitlesApi';
import CustomDateRangePicker from 'Components/CustomDateRangePicker/CustomDateRangePicker';
import reportJobsApi from 'Api/axios/report-jobs-api';
import { REPORT_TOAST_OPTIONS } from 'Components/ReportJobsWidget/toast-options';
import { useReportJobsStore } from 'stores/report-jobs-store';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import type { DateRangeValue } from 'types';
import { format } from 'date-fns';

interface MassiveExportDialogProps {
  open: boolean;
  onClose(): void;
}

const MassiveExportDialog = ({ open, onClose }: MassiveExportDialogProps) => {
  const coursesTableRef = useRef<CoursesTableHandle>(null);
  const [loading, setLoading] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);
  const upsertJob = useReportJobsStore(state => state.upsertJob);

  const {
    control,
    reset,
    getValues,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      terminalId: [],
      departmentId: null,
      jobTitleId: null,
      finishedAtRange: undefined as DateRangeValue | undefined,
    },
  });

  const { data: terminals } = useTerminalsApi({ init: true });

  const { data: departments } = useDepartmentsApi({ init: true });

  const { data: jobTitles } = useJobTitlesApi({ init: true });

  const onCloseDialog = () => {
    reset();
    onClose();
  };

  /**
   * Manda el reporte a generarse y suelta la pantalla.
   *
   * Antes esperaba aquí a que el Excel estuviera armado; con selecciones
   * grandes el navegador cortaba la petición por tiempo y el archivo nunca
   * llegaba, sin ningún aviso. Ahora el servidor avisa que lo recibió y el
   * avance se sigue en la ventana de la esquina.
   */
  const handleGenerateReport = async () => {
    const courseIds = coursesTableRef.current?.getSelectedIds() ?? [];
    if (courseIds.length === 0) return;

    const { terminalId, departmentId, jobTitleId, finishedAtRange } = getValues();

    setLoading(true);
    try {
      const { data, error, meta } = await reportJobsApi.createCoursesReportJob(courseIds, {
        terminalId,
        departmentId,
        jobTitleId,
        startFinishedAt: finishedAtRange?.startDate ? format(finishedAtRange.startDate, 'yyyy-MM-dd') : undefined,
        endFinishedAt: finishedAtRange?.endDate ? format(finishedAtRange.endDate, 'yyyy-MM-dd') : undefined,
      });

      if (error || !data) {
        toast.error(error ?? 'No se pudo iniciar la generación del reporte.', REPORT_TOAST_OPTIONS);
        return;
      }

      // La ventana de progreso aparece de inmediato, sin esperar al siguiente
      // sondeo.
      upsertJob(data);

      // Cuando el backend reutiliza un reporte en marcha hay que decirlo, o
      // parecería que el clic no hizo nada.
      const message = (meta as { alreadyRunning?: boolean } | null)?.alreadyRunning
        ? 'Ese reporte ya se está generando. La descarga iniciará automáticamente cuando termine.'
        : 'El reporte se está generando. La descarga iniciará automáticamente cuando el proceso finalice.';

      toast.info(message, REPORT_TOAST_OPTIONS);

      onCloseDialog();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onCloseDialog}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}
    >
      <DialogTitle>Generación de Reporte</DialogTitle>
      <Divider />
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1 }}>
        <Typography variant="body1" gutterBottom>
          Selecciona los cursos que deseas incluir en el reporte. Puedes aplicar filtros para refinar tu selección y
          asegurarte de que el reporte generado contenga solo la información relevante para ti.
        </Typography>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <CoursesTable ref={coursesTableRef} onSelectionChange={setSelectedCount} />
        </Box>
        <Box display="flex" flexDirection="row" gap="20px" alignItems="end" marginTop="20px" flexShrink={0}>
          <Controller
            name="finishedAtRange"
            control={control}
            render={({ field }) => (
              <CustomDateRangePicker
                label="Fecha de finalización"
                placeholder="Seleccionar rango"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />

          <Controller
            name="terminalId"
            control={control}
            render={({ field }) => (
              <CustomMultipleSelect
                placeholder={'Terminal'}
                options={terminals.map(terminal => ({ label: terminal.name, value: terminal.id }))}
                {...field}
              />
            )}
          />
          <Controller
            name="departmentId"
            control={control}
            render={({ field }) => (
              <CustomSelect
                placeholder={'Departamento'}
                options={departments.map(department => ({ label: department.name, value: department.id }))}
                {...field}
              />
            )}
          />
          <Controller
            name="jobTitleId"
            control={control}
            render={({ field }) => (
              <CustomSelect
                placeholder={'Puesto'}
                options={jobTitles.map(jobTitle => ({ label: jobTitle.name, value: jobTitle.id }))}
                {...field}
              />
            )}
          />
          {isDirty && (
            <Button variant="text" color="error" size="small" onClick={() => reset()}>
              Limpiar filtros
            </Button>
          )}
        </Box>
        <Divider sx={{ marginY: '20px' }} />
        <Box display="flex" flexDirection="row" gap="10px" justifyContent="end" flexShrink={0}>
          <Button variant="outlined" color="error" onClick={onCloseDialog}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleGenerateReport}
            disabled={loading || selectedCount === 0}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Generar Reporte'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default MassiveExportDialog;
